import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH21_QUESTIONS } from "../data/neetPhysicsCh21Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 21: Electromagnetic Induction (NCERT Class 12 Physics).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Magnetic Flux & Faraday's Law", "Lenz's Law & Motional EMF", "Self & Mutual Inductance", "Energy in Inductors", "AC Generator & Eddy Currents"];

const SECTION_COLORS = {
  "Magnetic Flux & Faraday's Law": "#2563eb",
  "Lenz's Law & Motional EMF":     "#7c3aed",
  "Self & Mutual Inductance":      "#dc2626",
  "Energy in Inductors":           "#16a34a",
  "AC Generator & Eddy Currents":  "#d97706",
};

const CONCEPTS = [
  // ── Magnetic Flux & Faraday's Law ──────────────────────────────────────────
  { section: "Magnetic Flux & Faraday's Law", title: "Magnetic flux — definition and formula (Φ = B·A·cosθ)",
    explanation: "Magnetic flux (Phi) through a surface measures the total magnetic field 'passing through' it. For a flat surface of area A in a uniform field B, Phi = B.A.cos(theta), where theta is the angle between the field B and the NORMAL (perpendicular) to the surface -- not the angle between B and the surface itself. Flux is maximum when B is along the normal (theta=0) and zero when B lies in the plane of the surface (theta=90 degrees). Its SI unit is the weber (Wb), where 1 Wb = 1 T.m^2.",
    example: `Phi = B . A . cos(theta)

Coil area A = 0.02 m^2, field B = 0.5 T,
angle between B and the normal, theta = 60 degrees

Phi = 0.5 x 0.02 x cos(60) = 0.5 x 0.02 x 0.5 = 5 x 10^-3 Wb` },
  { section: "Magnetic Flux & Faraday's Law", title: "Faraday's first law of electromagnetic induction",
    explanation: "Faraday's first law states that whenever the magnetic flux linked with a circuit changes -- because the field strength changes, the area changes, or the orientation (angle) changes -- an EMF is induced in the circuit, and this induced EMF lasts only as long as the flux keeps changing. This is the qualitative statement of electromagnetic induction; Faraday's second law makes it quantitative.",
    example: `Flux can change because:
1) B changes with time (electromagnet current varies)
2) A changes with time (loop is stretched/shrunk)
3) theta changes with time (coil rotates in a fixed field)

Any of these three changes alone is enough to induce an EMF` },
  { section: "Magnetic Flux & Faraday's Law", title: "Faraday's second law — the induced EMF equation",
    explanation: "Faraday's second law gives the MAGNITUDE of the induced EMF: it equals the rate of change of magnetic flux. For a coil of N identical turns, the flux linkage is N.Phi, so the induced EMF is e = -N (dPhi/dt). The negative sign (Lenz's law) shows the EMF opposes the change producing it; for magnitude calculations, the sign is often dropped.",
    example: `e = -N (dPhi/dt)

Coil of N = 100 turns:
Phi changes from 2 x 10^-3 Wb to 8 x 10^-3 Wb in 0.1 s
dPhi/dt = (8 - 2) x 10^-3 / 0.1 = 6 x 10^-2 Wb/s

|e| = N x dPhi/dt = 100 x 0.06 = 6 V` },
  { section: "Magnetic Flux & Faraday's Law", title: "Worked example: calculating magnetic flux",
    explanation: "Applying Phi = B.A.cos(theta) directly gives the flux through a loop for any given field strength, area, and orientation. Watch the angle carefully -- many problems state the angle between the field and the PLANE of the coil, which must first be converted to the angle with the normal (theta_normal = 90 - theta_plane) before using the formula.",
    example: `A rectangular coil, area A = 0.1 m^2, sits in a field B = 0.4 T
that is perpendicular to the coil's plane (along the normal), theta = 0

Phi = B . A . cos(0) = 0.4 x 0.1 x 1 = 0.04 Wb

If the same coil is tilted so the field makes 30 degrees with
the PLANE of the coil, then theta (with the normal) = 60 degrees:
Phi = 0.4 x 0.1 x cos(60) = 0.4 x 0.1 x 0.5 = 0.02 Wb` },
  { section: "Magnetic Flux & Faraday's Law", title: "Worked example: EMF induced by a changing magnetic field",
    explanation: "When a stationary coil sits in a magnetic field that changes with time (dB/dt), the induced EMF follows directly from e = N.A.(dB/dt), since A and theta stay fixed and only B varies. This is the situation inside a changing electromagnet or a transformer's secondary coil.",
    example: `e = N . A . (dB/dt)      (A, theta fixed; only B changes)

Circular coil: radius r = 5 cm = 0.05 m, N = 50 turns
Area A = pi r^2 = pi x (0.05)^2 = 7.85 x 10^-3 m^2
Field increasing at dB/dt = 0.2 T/s

e = 50 x 7.85 x 10^-3 x 0.2 = 0.0785 V = 78.5 mV` },

  // ── Lenz's Law & Motional EMF ───────────────────────────────────────────────
  { section: "Lenz's Law & Motional EMF", title: "Lenz's law and the conservation of energy",
    explanation: "Lenz's law states that the direction of an induced current is always such that it OPPOSES the change in magnetic flux that produced it. This is a direct consequence of conservation of energy: if the induced current instead assisted the change, the flux change would accelerate itself, generating more current and more energy from nothing -- an impossible perpetual motion machine. The work needed to overcome this opposition is exactly the source of the electrical energy delivered.",
    example: `If induced current INCREASED the flux change instead of opposing it:
more current -> faster flux change -> even more current -> ...
This runaway would create energy from nothing -- forbidden.

So: induced current always acts to oppose the flux change,
converting mechanical (or other) work into electrical energy.` },
  { section: "Lenz's Law & Motional EMF", title: "Direction of induced current — applying Lenz's law",
    explanation: "To find the induced current's direction: identify whether flux through the circuit is increasing or decreasing, then apply Lenz's law -- the induced current flows in whichever direction creates a magnetic field that opposes that change. When a magnet's north pole approaches a coil (flux towards the coil increasing), the coil's near face becomes a north pole (like poles repel, opposing the approach); when the magnet is withdrawn (flux decreasing), the near face becomes a south pole (unlike poles attract, opposing the departure).",
    example: `N-pole of a magnet moving TOWARDS a coil:
  flux into the coil is INCREASING
  -> induced current makes the near face a NORTH pole (repels magnet)

N-pole of the same magnet moving AWAY from the coil:
  flux into the coil is DECREASING
  -> induced current makes the near face a SOUTH pole (attracts magnet)

Either way, the induced effect opposes the magnet's motion` },
  { section: "Lenz's Law & Motional EMF", title: "Motional EMF — a rod moving through a magnetic field",
    explanation: "When a conducting rod of length l moves with velocity v through a uniform field B, with v, B and l mutually perpendicular, the magnetic force qvB pushes free charges toward one end of the rod, building up charge until the resulting electric force balances it. The EMF driving this charge separation is e = B.l.v. This 'motional EMF' is exactly equivalent to the flux-rule result for a rod sliding on rails, since sweeping the rod changes the enclosed loop's area at rate l.v.",
    example: `e = B . l . v

Force per unit charge on carriers in the rod = v x B
Work done moving a charge across the rod's length l:
e = (force per charge) x length = (vB) x l = Blv

This matches e = -dPhi/dt for a rod sliding on rails,
since dA/dt = l . v (the swept area grows at rate l.v)` },
  { section: "Lenz's Law & Motional EMF", title: "Worked example: motional EMF, current, and force on a moving rod",
    explanation: "Once the motional EMF is found from e = Blv, treating it like any other EMF source lets you find the circuit current (I = e/R) and, since a current-carrying rod in a field feels a force F = BIl, the force an external agent must supply to keep the rod moving at constant velocity (balancing this magnetic braking force).",
    example: `Rod: length l = 0.5 m, speed v = 4 m/s, field B = 0.2 T
e = B l v = 0.2 x 0.5 x 4 = 0.4 V

Connected to resistance R = 2 ohm:
I = e / R = 0.4 / 2 = 0.2 A

Force needed to maintain constant velocity:
F = B I l = 0.2 x 0.2 x 0.5 = 0.02 N` },
  { section: "Lenz's Law & Motional EMF", title: "Induced force always opposes motion (magnetic damping)",
    explanation: "The current induced in a moving rod always experiences a magnetic force F = BIl directed to OPPOSE the rod's motion (Lenz's law again). If no external force is applied, this retarding force steadily decelerates the rod. The mechanical power the external agent supplies (F.v) is always exactly balanced by the electrical power dissipated in the circuit's resistance (I^2 R) -- confirming energy is converted, not created.",
    example: `Using the previous rod: e = 0.4 V, I = 0.2 A, R = 2 ohm,
F = 0.02 N, v = 4 m/s

Mechanical power supplied = F . v = 0.02 x 4 = 0.08 W
Electrical power dissipated = I^2 R = (0.2)^2 x 2 = 0.08 W

Equal -- energy is conserved, just converted in form` },

  // ── Self & Mutual Inductance ────────────────────────────────────────────────
  { section: "Self & Mutual Inductance", title: "Self-inductance — definition and the henry",
    explanation: "Self-inductance (L) of a coil relates the EMF induced in the coil to the rate of change of the CURRENT FLOWING THROUGH THAT SAME COIL: e = -L (dI/dt). It measures how strongly a coil opposes changes in its own current, due to the changing flux that current itself produces. Its SI unit is the henry (H); a coil has an inductance of 1 H if a current changing at 1 A/s induces an EMF of 1 V.",
    example: `e = -L (dI/dt)

Coil with L = 2 H, current changes from 3 A to 5 A in 0.5 s
dI/dt = (5 - 3) / 0.5 = 4 A/s

|e| = L x dI/dt = 2 x 4 = 8 V` },
  { section: "Self & Mutual Inductance", title: "Self-inductance of a long solenoid",
    explanation: "For a long solenoid of N turns, length l, and cross-sectional area A, the field inside is B = mu0.N.I/l, so the flux linkage is N.Phi = N.B.A = mu0.N^2.A.I/l. Since L = (flux linkage)/I, this gives L = mu0.N^2.A/l -- self-inductance grows with the SQUARE of the number of turns, and depends only on the solenoid's geometry, not on the current flowing through it.",
    example: `B (inside solenoid) = mu0 . N . I / l
Flux linkage = N . Phi = N . B . A = mu0 N^2 A I / l

L = (flux linkage) / I = mu0 N^2 A / l

(depends only on N, A, l -- a property of the coil's geometry)` },
  { section: "Self & Mutual Inductance", title: "Worked example: self-inductance of a solenoid",
    explanation: "Substituting numbers directly into L = mu0.N^2.A/l gives the solenoid's inductance. Because N is squared, doubling the number of turns (for fixed length and area) quadruples the self-inductance.",
    example: `L = mu0 N^2 A / l     (mu0 = 4 pi x 10^-7 T.m/A)

N = 1000 turns, l = 0.5 m, A = 2 x 10^-3 m^2

L = (4 pi x 10^-7) x (1000)^2 x (2 x 10^-3) / 0.5
  = (1.2566 x 10^-6) x 10^6 x 2 x 10^-3 / 0.5
  = 1.2566 x 2 x 10^-3 / 0.5
  = 5.03 x 10^-3 H  (about 5 mH)` },
  { section: "Self & Mutual Inductance", title: "Mutual inductance — definition and dependence",
    explanation: "Mutual inductance (M) between two coils relates the EMF induced in ONE coil to the rate of change of current in the OTHER coil: e2 = -M (dI1/dt). It depends on both coils' geometry, their number of turns, their separation, and their relative orientation -- how much of the flux produced by coil 1 actually links coil 2. Its SI unit is also the henry.",
    example: `e2 = -M (dI1 / dt)

Two coils, M = 0.5 H
Current in coil 1 changes from 2 A to 6 A in 0.2 s
dI1/dt = (6 - 2) / 0.2 = 20 A/s

|e2| = M x dI1/dt = 0.5 x 20 = 10 V` },
  { section: "Self & Mutual Inductance", title: "Coefficient of coupling and worked example for M",
    explanation: "Not all the flux produced by one coil links the other -- the coefficient of coupling k (0 <= k <= 1) measures this efficiency, and M = k.sqrt(L1.L2). k = 1 only for perfect (ideal) coupling, such as two coils wound tightly on the same core; loosely spaced or poorly aligned coils have k close to 0.",
    example: `M = k . sqrt(L1 . L2)

L1 = 4 H, L2 = 9 H, k = 0.5

sqrt(L1 L2) = sqrt(4 x 9) = sqrt(36) = 6
M = 0.5 x 6 = 3 H` },

  // ── Energy in Inductors ─────────────────────────────────────────────────────
  { section: "Energy in Inductors", title: "Energy stored in an inductor (U = ½LI²)",
    explanation: "Establishing a current I in an inductor requires doing work against the back-EMF (self-induced EMF) that opposes the current's growth. The instantaneous power supplied is e.I = L(dI/dt).I; integrating this from 0 to a final current I gives the total work done, which is stored as magnetic energy in the inductor: U = (1/2) L I^2.",
    example: `Power supplied against back-emf = e . I = L (dI/dt) . I

U = integral from 0 to I of  L i di  = (1/2) L I^2

U = (1/2) L I^2   (analogous to (1/2) m v^2 for kinetic energy)` },
  { section: "Energy in Inductors", title: "Worked example: energy stored in an inductor",
    explanation: "Direct substitution into U = (1/2)LI^2 gives the stored magnetic energy for any inductor carrying a steady current. Since energy depends on I^2, doubling the current quadruples the stored energy for the same inductor.",
    example: `U = (1/2) L I^2

L = 4 H, I = 3 A

U = 0.5 x 4 x (3)^2 = 0.5 x 4 x 9 = 18 J` },
  { section: "Energy in Inductors", title: "Energy density of a magnetic field",
    explanation: "The energy stored in an inductor can be thought of as stored in the magnetic field filling its volume. For a field of magnitude B in vacuum (or air), the energy per unit volume (energy density) is u = B^2 / (2.mu0). This lets the energy in ANY region of space containing a magnetic field be estimated, not just inside a coil.",
    example: `u = B^2 / (2 mu0)     (mu0 = 4 pi x 10^-7 T.m/A)

For B = 1 T:
u = (1)^2 / (2 x 4 pi x 10^-7) = 1 / (2.513 x 10^-6)
  = 3.98 x 10^5 J/m^3` },
  { section: "Energy in Inductors", title: "Growth of current in an LR circuit — time constant",
    explanation: "When a steady EMF is switched on across an inductor L in series with resistance R, the current cannot jump instantly to its final value -- the inductor's back-EMF opposes the sudden change. The current grows as I(t) = I0 (1 - e^(-t/tau)), where tau = L/R is the circuit's time constant and I0 is the final steady current. After one time constant, the current has reached about 63.2% of its maximum value.",
    example: `I(t) = I0 (1 - e^(-t/tau))       tau = L/R

At t = tau:  I = I0 (1 - e^-1) = I0 (1 - 0.368) = 0.632 I0
             -> current reaches 63.2% of its final value

L = 2 H, R = 4 ohm  ->  tau = 2/4 = 0.5 s` },

  // ── AC Generator & Eddy Currents ────────────────────────────────────────────
  { section: "AC Generator & Eddy Currents", title: "AC generator — principle and construction",
    explanation: "An AC generator (alternator) converts mechanical energy into electrical energy using electromagnetic induction: a coil (the armature) is mechanically rotated inside a uniform magnetic field (or a magnet is rotated near a fixed coil), continuously changing the flux linked with the coil and inducing an alternating EMF. Key parts are the armature coil, strong field magnets, slip rings (which rotate with the coil) and brushes (fixed contacts) that connect the rotating coil to the external circuit.",
    example: `Rotating coil in field B, angular speed omega:
Phi(t) = B A cos(omega t)     (flux varies as coil rotates)

e(t) = -N dPhi/dt = N B A omega sin(omega t)

-- an alternating EMF, reversing direction every half rotation` },
  { section: "AC Generator & Eddy Currents", title: "EMF equation of an AC generator (e = e₀sinωt)",
    explanation: "The instantaneous EMF of an AC generator with N turns, area A, field B, rotating at angular speed omega is e = e0.sin(omega.t), where the peak EMF e0 = N.B.A.omega. The EMF is sinusoidal because it comes from differentiating the cosine variation of flux with the rotation angle omega.t. Angular speed relates to frequency by omega = 2.pi.f.",
    example: `e = e0 sin(omega t),   e0 = N B A omega

Doubling omega (spinning the coil faster), with N, B, A fixed,
doubles the peak EMF e0 -- but also doubles the AC frequency f` },
  { section: "AC Generator & Eddy Currents", title: "Worked example: peak and instantaneous EMF of a generator",
    explanation: "Given a generator's turns, area, field, and angular speed, e0 = NBAomega gives the peak EMF directly; substituting a specific time t into e = e0.sin(omega t) then gives the instantaneous EMF at that instant.",
    example: `e0 = N B A omega

N = 100, A = 0.05 m^2, B = 0.01 T, omega = 100 rad/s

e0 = 100 x 0.01 x 0.05 x 100 = 5 V

Instantaneous EMF at t = pi/600 s:
omega t = 100 x (pi/600) = pi/6,  sin(pi/6) = 0.5
e = 5 x 0.5 = 2.5 V` },
  { section: "AC Generator & Eddy Currents", title: "Eddy currents — definition and causes",
    explanation: "Eddy currents are induced currents that circulate within the BULK (volume) of a solid conductor -- not confined to a wire loop -- whenever the conductor experiences a changing magnetic flux (due to a changing field, or relative motion through a non-uniform field). Like any induced current, their direction (by Lenz's law) opposes the change causing them, and their flow through the conductor's resistance dissipates energy as heat (I^2 R losses).",
    example: `A solid metal plate swung into a magnetic field region:
- changing flux through the plate's bulk induces swirling
  (eddy) currents inside the metal
- these currents create a force opposing the plate's motion
  (magnetic braking) and heat the plate (I^2 R loss)` },
  { section: "AC Generator & Eddy Currents", title: "Eddy currents — applications and disadvantages",
    explanation: "Eddy currents are put to USEFUL work in induction furnaces (heating/melting metal directly via eddy-current I^2R heating), magnetic braking systems (trains, roller coasters), electromagnetic damping in galvanometers, and induction cooktops. However, in devices like transformers and motors, eddy currents in the iron core are a WASTEFUL energy loss (heating the core, reducing efficiency); this is minimized by building the core from thin, mutually insulated laminated sheets instead of a solid block, which greatly increases the resistance path for eddy currents without weakening the desired magnetic flux.",
    example: `Useful:   induction furnace, magnetic braking, induction cooktop
Wasteful: heating of transformer/motor iron cores (I^2 R loss)

Fix: replace solid core with thin insulated LAMINATIONS
     -> breaks up eddy current loops -> much smaller losses` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh21Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh21Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh21Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH21_QUESTIONS : NEET_PHYSICS_CH21_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 21</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Electromagnetic Induction</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12 Physics · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH21_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH21_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
